/**
 * ActivitiesFeed - Container principal do feed de atividades
 * Envolve a timeline e gerencia estado de carregamento
 * Implementa IntersectionObserver para scroll infinito
 * 
 * @version 2.1.0
 * RELEASE: 2026-02-09 v1.0.0 - Implementação inicial
 * RELEASE: 2026-02-10 v2.0.0 - Scroll infinito com IntersectionObserver
 * RELEASE: 2026-02-10 v2.1.0 - Fix: IntersectionObserver root aponta para container de scroll nativo
 */
import React, { useRef, useEffect, useCallback } from 'react';
import { Clock, Loader2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { ActivitiesTimeline } from './ActivitiesTimeline';
import type { FeedActivity } from '@/hooks/useActivitiesFeed';

// Props do componente
interface ActivitiesFeedProps {
  activities: FeedActivity[];
  isLoading: boolean;
  onComplete: (activityId: string) => void;
  onReopen: (activityId: string) => void;
  onDelete: (activityId: string) => void;
  /** v1.11.0: marca/desmarca como lido (Notas e Sistema) */
  onMarkRead?: (activityId: string) => void;
  onMarkUnread?: (activityId: string) => void;
  isCompleting: boolean;
  isReopening: boolean;
  isDeleting: boolean;
  isMarkingRead?: boolean;
  /** Callback para abrir modal de negócio */
  onOpenDeal?: (dealId: string) => void;
  /** Se true, os comentários iniciam expandidos por padrão */
  commentsDefaultExpanded?: boolean;
  /** v2.0.0: Se há mais páginas disponíveis para carregar */
  hasNextPage?: boolean;
  /** v2.0.0: Função para buscar próxima página */
  fetchNextPage?: () => void;
  /** v2.0.0: Se está carregando a próxima página */
  isFetchingNextPage?: boolean;
}

/**
 * Container do feed de atividades com scroll infinito
 */
export function ActivitiesFeed({
  activities,
  isLoading,
  onComplete,
  onReopen,
  onDelete,
  onMarkRead,
  onMarkUnread,
  isCompleting,
  isReopening,
  isDeleting,
  isMarkingRead,
  onOpenDeal,
  commentsDefaultExpanded = false,
  hasNextPage = false,
  fetchNextPage,
  isFetchingNextPage = false,
}: ActivitiesFeedProps) {
  // Ref do container de scroll nativo (substitui ScrollArea)
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Ref do sentinela para IntersectionObserver (scroll infinito)
  const sentinelRef = useRef<HTMLDivElement>(null);

  /**
   * IntersectionObserver para disparar fetchNextPage
   * quando o sentinela fica visível dentro do container de scroll
   * v2.1.0: Usa scrollContainerRef como root para funcionar com overflow nativo
   */
  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasNextPage && !isFetchingNextPage && fetchNextPage) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const scrollContainer = scrollContainerRef.current;
    if (!sentinel || !scrollContainer) return;

    const observer = new IntersectionObserver(handleIntersection, {
      // v2.1.0: root = container de scroll real (não document viewport)
      root: scrollContainer,
      rootMargin: '200px',
      threshold: 0,
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleIntersection]);

  // Estado de carregamento inicial (primeira página)
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="flex gap-3 p-4 rounded-xl border bg-card shadow-sm">
            <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <Skeleton className="h-7 w-7 rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  // Estado vazio
  if (activities.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="h-14 w-14 rounded-full bg-muted/50 flex items-center justify-center mb-4">
          <Clock className="h-7 w-7 text-muted-foreground" />
        </div>
        <p className="text-base font-medium text-muted-foreground">
          Nenhuma atividade encontrada
        </p>
        <p className="text-sm text-muted-foreground/70 mt-1 max-w-md">
          As atividades registradas nos negócios aparecerão aqui em formato de timeline
        </p>
      </div>
    );
  }

  return (
    <div ref={scrollContainerRef} className="h-full overflow-y-auto pr-3 scrollbar-thin">
      <ActivitiesTimeline
        activities={activities}
        onComplete={onComplete}
        onReopen={onReopen}
        onDelete={onDelete}
        onMarkRead={onMarkRead}
        onMarkUnread={onMarkUnread}
        isCompleting={isCompleting}
        isReopening={isReopening}
        isDeleting={isDeleting}
        isMarkingRead={isMarkingRead}
        onOpenDeal={onOpenDeal}
        commentsDefaultExpanded={commentsDefaultExpanded}
      />

      {/* Sentinela do IntersectionObserver para scroll infinito */}
      <div ref={sentinelRef} className="h-1" />

      {/* Spinner de carregamento da próxima página */}
      {isFetchingNextPage && (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">
            Carregando mais atividades...
          </span>
        </div>
      )}
    </div>
  );
}
